import type { ComponentProps, ReactNode } from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"
import { cn } from "@/lib/utils"
import { Shortcut } from "./kbd"

/**
 * One provider for the app. The first tooltip waits, so sweeping the pointer
 * across the interface shows nothing; once one is open its neighbours open
 * at once and without animation, which is what makes a toolbar feel fast.
 */
function TooltipProvider(props: ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider delayDuration={500} skipDelayDuration={300} {...props} />
}

/**
 * A label for its child, with the keyboard shortcut when there is one. Every
 * shortcut in the product is discoverable this way.
 */
function Tip({
  label,
  keys,
  side = "bottom",
  children,
}: {
  label: string
  keys?: string
  side?: ComponentProps<typeof TooltipPrimitive.Content>["side"]
  children: ReactNode
}) {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            "pop type-label z-50 flex items-center gap-2 rounded-md border bg-popover px-2 py-1 text-popover-foreground shadow-popover [--pop-origin:var(--radix-tooltip-content-transform-origin)]",
          )}
        >
          {label}
          {keys ? <Shortcut keys={keys} /> : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

export { Tip, TooltipProvider }
